import { HttpError } from 'react-admin';

/**
 * Traduction des erreurs de l'API en messages francais.
 *
 * Le backend repond uniformement `{"error": {"code": "..."}}` : un code machine,
 * jamais une phrase. Affiche tel quel, l'utilisateur lirait « forbidden » ou,
 * pire, `[object Object]` — ce que React Admin produit quand on lui passe le
 * corps brut. Cette table est donc la seule frontiere ou un code devient un
 * message.
 *
 * Les codes sont releves dans les controleurs HTTP du backend, sous
 * `backend/src/<Contexte>/Presentation/Http`. Un code inconnu retombe sur un message
 * generique **qui mentionne le code** : sans lui, un diagnostic serait
 * impossible ; avec lui, on garde une piste sans afficher un objet brut.
 */
const MESSAGES_BY_CODE: Readonly<Record<string, string>> = {
  unauthorized: 'Session expirée ou absente. Reconnectez-vous.',
  invalid_session: 'Session expirée ou absente. Reconnectez-vous.',
  invalid_credentials: 'Adresse e-mail ou mot de passe incorrect.',
  // Deux contextes, un seul code cote serveur. Le message reste volontairement
  // general : nommer « ce profil bebe » etait juste pour les neuf ressources
  // metier et faux partout dans l'administration, ou le refus porte sur un role
  // d'exploitation. Un message faux envoie chercher au mauvais endroit.
  forbidden: "Vous n'avez pas les droits nécessaires pour cette opération.",
  confirmation_mismatch:
    'La confirmation saisie ne correspond pas exactement à la valeur attendue. Rien n’a été fait.',
  // 403 et non 401 cote serveur : la session reste valide, seule la preuve
  // supplementaire d'une action lourde a ete refusee. Rien n'est deconnecte.
  reauthentication_failed:
    'Mot de passe incorrect. Rien n’a été fait : saisissez à nouveau votre mot de passe.',
  // 409 : l'etat a change depuis la lecture du client (revision d'un brouillon).
  conflict: 'Cette ressource a changé depuis que vous l’avez lue. Rien n’a été écrit.',
  // 422 de la publication d'un document legal : jamais ecrit, ou deja publie.
  no_draft: 'Il n’y a plus de brouillon à publier : il a peut-être déjà été publié.',
  maintenance_enabled:
    'Le service est en maintenance. Réessayez dans quelques minutes.',
  not_found: 'Ressource introuvable.',
  // 409 du registre des demandes RGPD : une cloture est la trace de la reponse faite.
  privacy_request_closed: 'Cette demande est clôturée : elle n’est plus modifiable.',
  validation_failed: 'Les données envoyées sont invalides. Vérifiez les champs du formulaire.',
  email_already_used: 'Cette adresse e-mail est déjà utilisée.',
  too_many_requests: 'Trop de tentatives. Patientez avant de réessayer.',
  rate_limited: 'Trop de tentatives. Patientez avant de réessayer.',
  weak_password: 'Le mot de passe est trop faible.',
  invalid_token: 'Ce lien ou ce jeton n’est plus valide.',
  insufficient_stock: 'Le stock disponible est insuffisant pour cette opération.',
  batch_not_found: 'Le lot de lait référencé est introuvable.',
  storage_location_not_found: 'Le lieu de stockage référencé est introuvable.',
  // Nom reel cote serveur (`AuthController`) : l'ancienne cle `mailer_unavailable`
  // n'a jamais ete emise, le message ne s'affichait donc jamais.
  mail_delivery_failed: "Le courriel n'a pas pu être envoyé. Réessayez dans un instant.",
  // 410 de `/auth/reset-password` : inconnu, deja consomme et expire sont
  // volontairement indiscernables cote serveur, le message l'est donc aussi.
  invalid_reset_token: "Ce code n'est plus valide ou a déjà servi.",
  // Refus d'une action sur un compte (`AdminUserActionRefusedException`, 422) :
  // rien n'a ete fait, et le message dit pourquoi.
  cannot_suspend_self: 'Vous ne pouvez pas suspendre votre propre compte. Rien n’a été fait.',
  cannot_suspend_administrator:
    'Ce compte porte un rôle d’administration : retirez-le d’abord en console avant de le suspendre. Rien n’a été fait.',
  cannot_anonymize_administrator:
    'Ce compte porte un rôle d’administration : retirez-le d’abord en console avant de l’anonymiser. Rien n’a été fait.',
  // 422 de l'anonymisation (ADR 0037, point 9) : la propriete se transmet d'abord,
  // par la titulaire — l'administration ne la transfere pas a sa place.
  owner_has_members:
    'Ce compte est propriétaire d’un foyer qui a d’autres membres : la propriété doit d’abord être transférée. Rien n’a été fait.',
  already_suspended: 'Ce compte est déjà suspendu. Rien n’a été fait.',
  not_suspended: 'Ce compte n’est pas suspendu. Rien n’a été fait.',
};

/** Message pose quand le serveur n'a meme pas repondu (reseau coupe, CORS refuse). */
export const NETWORK_ERROR_MESSAGE =
  "L'API est injoignable. Vérifiez qu'elle est démarrée et qu'elle autorise l'origine de ce backoffice (CORS_ALLOWED_ORIGINS).";

/** Message pose quand le statut n'a aucun corps exploitable. */
const messageForStatus = (status: number): string => {
  if (status >= 500) {
    return 'Le serveur a rencontré une erreur. Réessayez dans un instant.';
  }

  if (status === 405) {
    return "Cette opération n'est pas exposée par l'API.";
  }

  return `La requête a échoué (statut HTTP ${String(status)}).`;
};

/**
 * Extrait le code d'erreur d'un corps de reponse, sans supposer sa forme.
 *
 * Accepte les deux formes rencontrees : `{error: {code}}` pour les controleurs
 * de ce projet, et `{"hydra:description"}` / `{detail}` pour les reponses
 * generees par API Platform lui-meme (documentation, erreurs de serialisation).
 */
export const extractErrorCode = (body: unknown): string | null => {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const error: unknown = (body as Record<string, unknown>).error;

  if (typeof error === 'object' && error !== null) {
    const code: unknown = (error as Record<string, unknown>).code;

    if (typeof code === 'string' && code !== '') {
      return code;
    }
  }

  // Certaines couches renvoient directement `{"error": "code"}`.
  if (typeof error === 'string' && error !== '') {
    return error;
  }

  return null;
};

/** Description libre eventuellement fournie par API Platform. */
const extractDescription = (body: unknown): string | null => {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const record = body as Record<string, unknown>;

  for (const key of ['hydra:description', 'detail', 'message', 'title']) {
    const value: unknown = record[key];

    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }

  return null;
};

/**
 * Traduit un code d'erreur backend en message francais.
 *
 * Exportee pour que les tests et l'ecran de connexion puissent l'utiliser sans
 * fabriquer une reponse HTTP.
 */
export const messageForErrorCode = (code: string): string =>
  MESSAGES_BY_CODE[code] ?? `Une erreur est survenue (code « ${code} »).`;

/**
 * Construit l'erreur que React Admin sait afficher.
 *
 * `HttpError` est le seul type que React Admin traite comme une erreur metier :
 * il en tire la notification, et `body` alimente les erreurs par champ des
 * formulaires. Rendre un `Error` nu ferait apparaitre « Erreur inconnue ».
 */
export const toHttpError = (status: number, body: unknown): HttpError => {
  const code = extractErrorCode(body);
  const message =
    code !== null ? messageForErrorCode(code) : (extractDescription(body) ?? messageForStatus(status));

  return new HttpError(message, status, body);
};

/** Erreur de transport : aucune reponse HTTP n'est parvenue. */
export const toNetworkError = (cause: unknown): HttpError => {
  const error = new HttpError(NETWORK_ERROR_MESSAGE, 0, cause);

  return error;
};
